import { useEffect, useState, useMemo } from "react";
import { Modal, Loader } from "components";
import { client_v2 } from "utils/api";
import { toastr } from "react-redux-toastr";
import StyledUserPointsModal from "./styles";

const UserPointsModal = ({ user_id, date, handleClose }) => {
  const [is_loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    getData();
  }, [date, user_id]);

  const getData = async () => {
    try {
      const res = await client_v2.get(
        `/stats/user_day_points?user_id=${user_id}&date=${date}`,
      );
      setData(res.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleClose();
      toastr.error("Błąd", "Wystąpił błąd podczas pobierania danych");
    }
  };

  const total_picked_products_count = useMemo(() => {
    if (!data?.picking) return 0;
    return Object.values(data?.picking).reduce((acc, acu) => {
      return parseFloat(acu.picked_products_count) + acc;
    }, 0);
  }, [data?.picking]);

  const total_picked_quantity = useMemo(() => {
    if (!data?.picking) return 0;
    return Object.values(data?.picking).reduce((acc, acu) => {
      return parseFloat(acu.picked_quantity) + acc;
    }, 0);
  }, [data?.picking]);

  const total_picking_points = useMemo(() => {
    if (!data?.picking) return 0;
    return Object.values(data?.picking).reduce((acc, acu) => {
      return parseFloat(acu.total_points) + acc;
    }, 0);
  }, [data?.picking]);

  const total_packing_points = useMemo(() => {
    if (!data?.packing) return 0;
    return Object.values(data?.packing).reduce((acc, acu) => {
      return (
        acu
          .map((item) => item.total_points)
          .reduce((acc, acu) => {
            return parseFloat(acu) + acc;
          }, 0) + acc
      );
    }, 0);
  }, [data?.packing]);

  const total_storing_points = useMemo(() => {
    if (!data?.storing) return 0;
    return Object.values(data?.storing).reduce((acc, acu) => {
      return parseFloat(acu.total_points) + acc;
    }, 0);
  }, [data?.storing]);

  return (
    <Modal
      no_padding
      size="lg"
      handleClose={handleClose}
      header={{
        title: `Punkty z dnia ${date || ""}`,
      }}
    >
      {is_loading ? (
        <div className="row jc-c p-30">
          <Loader />
        </div>
      ) : (
        <StyledUserPointsModal>
          {!!data?.storing && Object.keys(data?.storing).length > 0 && (
            <>
              <p className="title">Dostawy</p>
              <table className="table table-sm">
                <thead className="table__header">
                  <tr>
                    <th>Identyfikator dostawy</th>
                    <th>Ilość rozniesionych produktów</th>
                    <th>Punkty</th>
                  </tr>
                </thead>
                <tbody className="table__body">
                  {Object.values(data?.storing).map((storing_item) => (
                    <tr key={storing_item.grn_id}>
                      <td>
                        <a
                          className="link"
                          target="_blank"
                          href={`/grns/${storing_item.grn_id}`}
                        >
                          {storing_item.grn_identifier}
                        </a>
                      </td>
                      <td>{storing_item.stored_quantity}</td>
                      <td>
                        {parseFloat(storing_item.total_points).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2}>
                      <strong>Razem:</strong>
                    </td>
                    <td>
                      <strong>
                        {parseFloat(total_storing_points).toFixed(2)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {!!data?.picking && Object.keys(data?.picking).length > 0 && (
            <>
              <p className="title">Zbieranie</p>
              <table className="table table-sm">
                <thead className="table__header">
                  <tr>
                    <th>Godzina zebrania</th>
                    <th>Id paka</th>
                    <th>Wózek</th>
                    <th>Ilość lokalizacji</th>
                    <th>Ilość produktów</th>
                    <th>Punkty</th>
                  </tr>
                </thead>
                <tbody className="table__body">
                  {Object.keys(data?.picking).map((gdn_pack_identifier) => {
                    const picking_item = data?.picking[gdn_pack_identifier];
                    if (!picking_item) return null;
                    return (
                      <tr key={gdn_pack_identifier}>
                        <td>{picking_item.picked_at}</td>
                        <td>{gdn_pack_identifier}</td>
                        <td>{picking_item.cart_identifier || "bez wózka"}</td>
                        <td>{picking_item.picked_products_count}</td>
                        <td>{picking_item.picked_quantity}</td>
                        <td>
                          {parseFloat(picking_item.total_points).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={3}>
                      <strong>Razem:</strong>
                    </td>
                    <td>
                      <strong>{total_picked_products_count}</strong>
                    </td>
                    <td>
                      <strong>{total_picked_quantity}</strong>
                    </td>
                    <td>
                      <strong>
                        {parseFloat(total_picking_points).toFixed(2)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {!!data?.packing && Object.keys(data?.packing).length > 0 && (
            <>
              <p className="title">Pakowanie</p>
              <table className="table table-sm">
                <thead className="table__header">
                  <tr>
                    <th>Godzina</th>
                    <th>Id zamówienia</th>
                    <th>Rozmiar zamówienia</th>
                    <th>Punkty</th>
                  </tr>
                </thead>
                <tbody className="table__body">
                  {Object.keys(data?.packing).map((date_key) => {
                    const packing_items = data?.packing[date_key];
                    if (!packing_items.length) return null;
                    return packing_items.map((packing_item) => (
                      <tr key={packing_item.id}>
                        <td>{date_key}</td>
                        <td>{packing_item.gdn_identifier}</td>
                        <td>{packing_item.cart_box_size}</td>
                        <td>{packing_item.total_points}</td>
                      </tr>
                    ));
                  })}
                  <tr>
                    <td colSpan={3}>
                      <strong>Razem:</strong>
                    </td>
                    <td>
                      <strong>
                        {parseFloat(total_packing_points).toFixed(2)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </StyledUserPointsModal>
      )}
    </Modal>
  );
};

export default UserPointsModal;
